/**
 * Placeholder name handling for invitation-based signups.
 *
 * When a user is invited during onboarding we only collect their email, but the
 * user-creation endpoint requires a first/last name. We therefore send a known
 * placeholder ("New Member") and treat it as "name not yet provided" on the
 * frontend: it is never displayed, and the invited user is forced to enter their
 * real name in the onboarding Personalize step (which overwrites the placeholder
 * via the update-name endpoint).
 *
 * NOTE: This is a frontend sentinel for now. Once the dedicated non-admin invite
 * endpoint exposes an explicit `name_pending` flag on the user record, prefer
 * that flag over string matching and keep these constants only for sending.
 */
export const PLACEHOLDER_FIRST_NAME = "New";
export const PLACEHOLDER_LAST_NAME = "Member";

/**
 * The invite endpoint requires `dob`/`height_cm`/`weight_kg` even though they
 * are meaningless for a staff invitation. We send the same fixed placeholders
 * the Admin Panel uses for its hidden form fields so backend validation passes.
 * dob is a `YYYY-MM-DD` string; height/weight are strings (cm/kg).
 */
export const PLACEHOLDER_DOB = "1990-01-01";
export const PLACEHOLDER_HEIGHT_CM = "180";
export const PLACEHOLDER_WEIGHT_KG = "70";

/**
 * Returns true when the given first/last name pair is the invitation
 * placeholder (i.e. the user has not yet entered their real name).
 */
export const isPlaceholderName = (
  firstName?: string | null,
  lastName?: string | null
): boolean => {
  const first = (firstName ?? "").trim().toLowerCase();
  const last = (lastName ?? "").trim().toLowerCase();
  return (
    first === PLACEHOLDER_FIRST_NAME.toLowerCase() &&
    last === PLACEHOLDER_LAST_NAME.toLowerCase()
  );
};
